import { Steps } from "antd";
import { Formik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import ReactQuill from "react-quill-new";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import Breadcrumbs from "../../../components/Breadcrumbs";
import Button from "../../../components/button";
import FileUpload from "../../../components/FileUpload";
import Input from "../../../components/input";
import Select from "../../../components/Select";
import {
  clearVehicleDropdownState,
  fetchVehicleCompanyBranchesDropdown,
  fetchVehicleMakesDropdown,
  fetchVehicleModelsDropdown,
  fetchVehicleTransmissionTypesDropdown,
  fetchVehicleTypesDropdown,
  fetchVehicleVendorsDropdown,
} from "../../../store/Slices/VehicleDropdownSlice";
import { apiService } from "../../../services/apiService";
import "../../../styles/Access.css";
import "../../../styles/AssetsRegistration.css";
import "../../../styles/Issue.css";
import "../../../styles/Trip.css";
import {
  findCollectionByKeys,
  findOptionByValueOrLabel,
  formats,
  modules,
  normalizeOptions,
} from "../../../utils/helper";
import { errorNotify, successNotify } from "../../../utils/toast";
import {
  branchOptions,
  coverageTypeOptions,
  getNextVehicleSystemId,
  installmentFrequencyOptions,
  insuranceProviderOptions,
  paymentMethodOptions,
  trackingPackageOptions,
  paymentTypeOptions,
  transmissionTypeOptions,
  vehicleMakeOptions,
  vehicleModelOptions,
  vehicleStatusOptions,
  vehicleTypeOptions,
  vendorOptions,
  contractTypeOptions,
  billingFrequencyOptions,
  fuelTypeOptions,
  depreciationMethodOptions,
} from "./vehicleAssets.constants";

const normalizeAssetsRegistrationTab = (value, fallback = "vehicles") => {
  const normalized = String(value || "").trim().toLowerCase();
  return ["vehicles", "equipments", "tyres"].includes(normalized)
    ? normalized
    : fallback;
};

const getAssetsRegistrationPath = (tab = "vehicles") =>
  `/assets?tab=${normalizeAssetsRegistrationTab(tab)}`;

const getAssetsRegistrationReturnTab = (location, fallback = "vehicles") => {
  const params = new URLSearchParams(location?.search || "");
  return normalizeAssetsRegistrationTab(
    params.get("tab") || location?.state?.activeTab || location?.state?.returnTab,
    fallback
  );
};

const stripHtml = (value) =>
  String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();

const isInstallmentPayment = (value) =>
  String(value || "").trim().toLowerCase() === "installments";

const hasImeiTrackerValue = (value) => String(value || "").trim() !== "";

const toNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const mapFiles = (files = []) =>
  (files || [])
    .map((file) => file?.fileName || file?.file_path || file?.path || file)
    .filter(Boolean);

const findOptionWithFallback = (primaryOptions = [], fallbackOptions = [], value = "") =>
  findOptionByValueOrLabel(primaryOptions, value) ||
  findOptionByValueOrLabel(fallbackOptions, value);

const resolveOptionId = (primaryOptions = [], fallbackOptions = [], value = "") => {
  const matchedOption = findOptionWithFallback(primaryOptions, fallbackOptions, value);
  const directNumber = toNumber(matchedOption?.value, NaN);

  if (Number.isFinite(directNumber) && directNumber > 0) {
    return directNumber;
  }

  const sourceOptions = primaryOptions.length ? primaryOptions : fallbackOptions;
  const matchedIndex = sourceOptions.findIndex(
    (option) =>
      String(option?.value || "").toLowerCase() === String(value || "").toLowerCase() ||
      String(option?.label || "").toLowerCase() === String(value || "").toLowerCase()
  );

  if (matchedIndex >= 0) {
    return matchedIndex + 1;
  }

  const numericValue = toNumber(value, NaN);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

const getOptionFormValue = (option, fallbackOptions = []) => {
  if (!option) return "";

  const fallbackMatch =
    findOptionByValueOrLabel(fallbackOptions, option?.value) ||
    findOptionByValueOrLabel(fallbackOptions, option?.label);

  if (fallbackMatch) {
    return fallbackMatch.value;
  }

  const numericValue = toNumber(option?.value, NaN);
  if (Number.isFinite(numericValue) && String(option?.label || "").trim()) {
    return option.label;
  }

  return option?.value ?? option?.label ?? "";
};

const buildCreatePurchasedVehiclePayload = ({
  values,
  vehicleTypeApiOptions,
  branchApiOptions,
  vendorApiOptions,
  makeApiOptions,
  modelApiOptions,
  transmissionApiOptions,
}) => {
  const payload = {
    registration_number: String(values.registrationNumber || "").trim(),
    vehicle_type_id: resolveOptionId(vehicleTypeApiOptions, vehicleTypeOptions, values.vehicleType),
    chassis_number: String(values.vinChassisNo || "").trim(),
    fuel_type: String(values.fuelType || "").trim(),
    status: String(values.status || "").trim(),
    odm: toNumber(values.odometerReading),
    make_id: resolveOptionId(makeApiOptions, vehicleMakeOptions, values.make),
    model_id: resolveOptionId(modelApiOptions, vehicleModelOptions, values.model),
    manufacturing_year: toNumber(values.year),
    engine_capacity: toNumber(values.engineCapacity),
    imeino: String(values.imeino || "").trim() || null,
    tank_capacity: toNumber(values.tankCapacity),
    transmission_type: resolveOptionId(
      transmissionApiOptions,
      transmissionTypeOptions,
      values.transmissionType
    ),
    branch_id: resolveOptionId(branchApiOptions, branchOptions, values.branch),
    initial_fuel_efficiency: toNumber(values.initialFuelEfficiency),
    number_of_axles: toNumber(values.numberOfAxles),
    description: values.description || "",
    notes: stripHtml(values.notes),
    ...(String(values.trackingPackage || "").trim()
      ? { tracking_package: String(values.trackingPackage || "").trim().toLowerCase() }
      : {}),
    asset_purchases: {
      purchase_at: values.purchaseDate || "",
      purchase_cost: toNumber(values.purchaseCost),
      payment_type: String(values.paymentType || "").trim(),
      payment_method: String(values.paymentMethod || "").trim(),
      vendor_id: resolveOptionId(vendorApiOptions, vendorOptions, values.vendor),
      depreciation_method: String(values.depreciationMethod || "").trim(),
      useful_life: toNumber(values.usefulLife),
      residual_value: toNumber(values.residualValue),
    },
    asset_insurances: {
      provider: String(values.insuranceProvider || "").trim(),
      policy_number: String(values.policyNumber || "").trim(),
      coverage_type: String(values.coverageType || "").trim(),
      coverage_amount: toNumber(values.coverageAmount),
      premium_amount: toNumber(values.premiumAmount),
      start_date: values.insuranceStartDate || "",
      expiry_date: values.insuranceExpiryDate || "",
    },
    files: mapFiles(values.attachments),
  };

  if (isInstallmentPayment(values.paymentType)) {
    payload.asset_installments = {
      total_installments: toNumber(values.totalInstallments),
      installment_amount: toNumber(values.installmentAmount),
      installment_frequency: String(values.installmentFrequency || "").trim(),
      first_payment_date: values.firstPaymentDate || "",
      interest_rate: toNumber(values.interestRate),
      financing_institution: String(values.financingInstitution || "").trim(),
    };
  }

  return payload;
};

const getInitialValues = (editingVehicle) => {
  const systemId = editingVehicle?.systemId || getNextVehicleSystemId();
  const extra = editingVehicle?.formData || {};

  return {
    systemId,
    registrationNumber: editingVehicle?.registrationNumber || extra.registrationNumber || "",
    vinChassisNo: editingVehicle?.vinChassisNo || extra.vinChassisNo || "",
    make: editingVehicle?.make || extra.make || "",
    model: editingVehicle?.model || extra.model || "",
    year: editingVehicle?.year || extra.year || "",
    vehicleType: extra.vehicleType || "",
    fuelType: extra.fuelType || "",
    odometerReading: extra.odometerReading || "",
    engineCapacity: extra.engineCapacity || "",
    transmissionType: extra.transmissionType || "",
    tankCapacity: extra.tankCapacity || "",
    initialFuelEfficiency: extra.initialFuelEfficiency || "",
    numberOfAxles: extra.numberOfAxles || "",
    imeino: editingVehicle?.imeino || extra.imeino || "",
    trackingPackage:
      editingVehicle?.trackingPackage ||
      editingVehicle?.tracking_package ||
      extra.trackingPackage ||
      extra.tracking_package ||
      "",
    branch: editingVehicle?.branch || extra.branch || "",
    status: editingVehicle?.status || extra.status || "active",
    description: extra.description || "",

    purchaseDate: extra.purchaseDate || "",
    purchaseCost: extra.purchaseCost || editingVehicle?.totalCost || "",
    paymentType: extra.paymentType || "",
    paymentMethod: extra.paymentMethod || "",
    vendor: extra.vendor || "",
    depreciationMethod: extra.depreciationMethod || "",
    usefulLife: extra.usefulLife || "",
    residualValue: extra.residualValue || "",

    totalInstallments: extra.totalInstallments || "",
    installmentAmount: extra.installmentAmount || "",
    installmentFrequency: extra.installmentFrequency || "",
    firstPaymentDate: extra.firstPaymentDate || "",
    interestRate: extra.interestRate || "",
    financingInstitution: extra.financingInstitution || "",

    insuranceProvider: extra.insuranceProvider || "",
    policyNumber: extra.policyNumber || "",
    coverageType: extra.coverageType || "",
    coverageAmount: extra.coverageAmount || "",
    premiumAmount: extra.premiumAmount || "",
    insuranceStartDate: extra.insuranceStartDate || "",
    insuranceExpiryDate: extra.insuranceExpiryDate || editingVehicle?.nextServiceDue || "",

    attachments: extra.attachments || [],
    notes: extra.notes || editingVehicle?.notes || "",

    // Hired Fields
    contractType: extra.contractType || "",
    contractStartDate: extra.contractStartDate || "",
    contractEndDate: extra.contractEndDate || "",
    hireRate: extra.hireRate || "",
    billingFrequency: extra.billingFrequency || "",
    securityDeposit: extra.securityDeposit || "",
    maxKmAllowance: extra.maxKmAllowance || "",
  };
};

const getVehicleStepSchemas = (isHired) => [
  Yup.object().shape({
    registrationNumber: Yup.string().required("Registration No. is required"),
    vinChassisNo: Yup.string().required("VIN / Chassis No. is required"),
    make: Yup.string().required("Make is required"),
    model: Yup.string().required("Model is required"),
    year: Yup.number()
      .typeError("Year must be numeric")
      .min(1900, "Year must be valid")
      .max(2100, "Year must be valid")
      .required("Year is required"),
    fuelType: Yup.string().required("Fuel Type is required"),
    vehicleType: Yup.string().required("Vehicle Type is required"),
    odometerReading: Yup.number()
      .typeError("Odometer Reading must be numeric")
      .required("Odometer Reading is required"),
    engineCapacity: Yup.number()
      .typeError("Engine Capacity must be numeric")
      .positive("Engine Capacity must be greater than zero")
      .required("Engine Capacity is required"),
    initialFuelEfficiency: Yup.number()
      .typeError("Initial Fuel Efficiency must be numeric")
      .required("Initial Fuel Efficiency is required"),
    numberOfAxles: Yup.number()
      .typeError("Number of Axles must be numeric")
      .required("Number of Axles is required"),
    transmissionType: Yup.string().required("Transmission Type is required"),
    tankCapacity: Yup.number()
      .typeError("Tank Capacity must be numeric")
      .positive("Tank Capacity must be greater than zero")
      .required("Tank Capacity is required"),
    branch: Yup.string().required("Branch is required"),
    status: Yup.string().required("Status is required"),
    trackingPackage: Yup.string().when("imeino", {
      is: (value) => hasImeiTrackerValue(value),
      then: (schema) =>
        schema.required("Tracking Package is required when IMEI Tracker Code is provided"),
      otherwise: (schema) => schema.notRequired(),
    }),
    description: Yup.string()
      .test("description-max", "Description too long", (value) => stripHtml(value).length <= 1000)
      .nullable(),
  }),
  isHired
    ? Yup.object().shape({
      vendor: Yup.string().required("Vendor / Owner is required"),
      contractType: Yup.string().required("Contract Type is required"),
      contractStartDate: Yup.date().required("Contract Start Date is required"),
      contractEndDate: Yup.date()
        .min(Yup.ref("contractStartDate"), "Contract End Date must be after Start Date")
        .required("Contract End Date is required"),
      hireRate: Yup.number()
        .typeError("Hire Rate must be numeric")
        .positive("Hire Rate must be greater than zero")
        .required("Hire Rate is required"),
      billingFrequency: Yup.string().required("Billing Frequency is required"),
      securityDeposit: Yup.number()
        .typeError("Security Deposit must be numeric")
        .min(0, "Security Deposit cannot be negative")
        .required("Security Deposit is required"),
      maxKmAllowance: Yup.number()
        .typeError("Max KM Allowance must be numeric")
        .positive("Max KM Allowance must be greater than zero")
        .required("Max KM Allowance is required"),
    })
    : Yup.object().shape({
      purchaseDate: Yup.date().required("Purchase Date is required"),
      purchaseCost: Yup.number()
        .typeError("Purchase Cost must be numeric")
        .positive("Purchase Cost must be greater than zero")
        .required("Purchase Cost is required"),
      paymentType: Yup.string().required("Payment Type is required"),
      paymentMethod: Yup.string().required("Payment Method is required"),
      vendor: Yup.string().required("Vendor is required"),
      depreciationMethod: Yup.string().required("Depreciation Method is required"),
      usefulLife: Yup.number()
        .typeError("Useful Life must be numeric")
        .positive("Useful Life must be greater than zero")
        .required("Useful Life is required"),
      residualValue: Yup.number()
        .typeError("Residual Value must be numeric")
        .min(0, "Residual Value cannot be negative")
        .required("Residual Value is required"),
      totalInstallments: Yup.number()
        .typeError("Total Installments must be numeric")
        .integer("Total Installments must be an integer")
        .positive("Total Installments must be greater than zero")
        .when("paymentType", {
          is: "installments",
          then: (schema) => schema.required("Total Installments is required"),
          otherwise: (schema) => schema.notRequired(),
        }),
      installmentAmount: Yup.number()
        .typeError("Installment Amount must be numeric")
        .positive("Installment Amount must be greater than zero")
        .when("paymentType", {
          is: "installments",
          then: (schema) => schema.required("Installment Amount is required"),
          otherwise: (schema) => schema.notRequired(),
        }),
      installmentFrequency: Yup.string().when("paymentType", {
        is: "installments",
        then: (schema) => schema.required("Installment Frequency is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      firstPaymentDate: Yup.date().when("paymentType", {
        is: "installments",
        then: (schema) => schema.required("First Payment Date is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      interestRate: Yup.number()
        .typeError("Interest Rate must be numeric")
        .min(0, "Interest Rate cannot be negative")
        .when("paymentType", {
          is: "installments",
          then: (schema) => schema.required("Interest Rate is required"),
          otherwise: (schema) => schema.notRequired(),
        }),
      financingInstitution: Yup.string().when("paymentType", {
        is: "installments",
        then: (schema) => schema.required("Financing Institution is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
    }),
  Yup.object().shape({
    insuranceProvider: Yup.string().required("Insurance Provider is required"),
    policyNumber: Yup.string().required("Policy Number is required"),
    coverageType: Yup.string().required("Coverage Type is required"),
    coverageAmount: Yup.number()
      .typeError("Coverage Amount must be numeric")
      .positive("Coverage Amount must be greater than zero")
      .required("Coverage Amount is required"),
    premiumAmount: Yup.number()
      .typeError("Premium Amount must be numeric")
      .positive("Premium Amount must be greater than zero")
      .required("Premium Amount is required"),
    insuranceStartDate: Yup.date().required("Start Date is required"),
    insuranceExpiryDate: Yup.date()
      .min(Yup.ref("insuranceStartDate"), "Expiry Date must be after Start Date")
      .required("Expiry Date is required"),
  }),
  Yup.object().shape({
    attachments: Yup.array().min(1, "At least one attachment is required"),
  }),
];

const getStepFields = (isHired) => [
  [
    "registrationNumber",
    "vinChassisNo",
    "make",
    "model",
    "year",
    "vehicleType",
    "fuelType",
    "engineCapacity",
    "odometerReading",
    "tankCapacity",
    "transmissionType",
    "branch",
    "initialFuelEfficiency",
    "status",
    "numberOfAxles",
    "imeino",
    "trackingPackage",
    "description",
  ],
  isHired
    ? [
      "vendor",
      "contractType",
      "contractStartDate",
      "contractEndDate",
      "hireRate",
      "billingFrequency",
      "securityDeposit",
      "maxKmAllowance",
    ]
    : [
      "purchaseDate",
      "purchaseCost",
      "paymentType",
      "paymentMethod",
      "vendor",
      "depreciationMethod",
      "usefulLife",
      "residualValue",
      "totalInstallments",
      "installmentAmount",
      "installmentFrequency",
      "firstPaymentDate",
      "interestRate",
      "financingInstitution",
    ],
  [
    "insuranceProvider",
    "policyNumber",
    "coverageType",
    "coverageAmount",
    "premiumAmount",
    "insuranceStartDate",
    "insuranceExpiryDate",
  ],
  ["attachments"],
];

const getFormSteps = (isHired) => [
  { title: "General Info" },
  { title: isHired ? "Hire/Contract" : "Purchase Info" },
  { title: "Insurance Info" },
  { title: "Attachment" },
];

const CreatePurchasedVehicle = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isHired = false;
  const returnPath = getAssetsRegistrationPath(
    getAssetsRegistrationReturnTab(location, "vehicles")
  );

  const editingVehicle = location?.state?.vehicle || null;
  const isEditing = Boolean(editingVehicle?.systemId);
  const {
    vehicleTypesDropdownData,
    vehicleTransmissionTypesDropdownData,
    vehicleMakesDropdownData,
    vehicleModelsDropdownData,
    vehicleCompanyBranchesDropdownData,
    vehicleVendorsDropdownData,
    vehicleDropdownLoadingCount,
    vehicleDropdownError,
  } = useSelector((state) => state.vehicleDropdowns);

  const [currentFormStep, setCurrentFormStep] = useState(0);

  useEffect(() => {
    dispatch(clearVehicleDropdownState());
    dispatch(fetchVehicleTypesDropdown());
    dispatch(fetchVehicleTransmissionTypesDropdown());
    dispatch(fetchVehicleMakesDropdown());
    dispatch(fetchVehicleModelsDropdown());
    dispatch(fetchVehicleCompanyBranchesDropdown());
    dispatch(fetchVehicleVendorsDropdown());

    return () => {
      dispatch(clearVehicleDropdownState());
    };
  }, [dispatch]);

  const formStepsList = useMemo(() => getFormSteps(isHired), [isHired]);
  const stepSchemasList = useMemo(() => getVehicleStepSchemas(isHired), [isHired]);
  const stepFieldsList = useMemo(() => getStepFields(isHired), [isHired]);

  const initialValues = useMemo(
    () => getInitialValues(editingVehicle, isHired),
    [editingVehicle, isHired]
  );

  const dropdownLoading = vehicleDropdownLoadingCount > 0;
  const resolvedDropdownError = vehicleDropdownError;

  const vehicleTypeApiOptions = useMemo(() => {
    const source = Array.isArray(vehicleTypesDropdownData)
      ? vehicleTypesDropdownData
      : findCollectionByKeys(vehicleTypesDropdownData, [
        "vehicletypes",
        "vehicletype",
        "vehicletypedropdown",
        "types",
      ]);

    const normalized = normalizeOptions(source, {
      valueKeys: ["value", "id", "vehicle_type_id"],
      labelKeys: ["label", "name", "title", "vehicle_type_name"],
      defaultLabelPrefix: "Vehicle Type",
    });

    return normalized.length ? normalized : vehicleTypeOptions;
  }, [vehicleTypesDropdownData]);

  const branchApiOptions = useMemo(() => {
    const source = Array.isArray(vehicleCompanyBranchesDropdownData)
      ? vehicleCompanyBranchesDropdownData
      : findCollectionByKeys(vehicleCompanyBranchesDropdownData, [
        "companybranches",
        "branches",
        "branchdropdown",
      ]);

    const normalized = normalizeOptions(source, {
      valueKeys: ["value", "id", "branch_id"],
      labelKeys: ["label", "branch_name", "name", "title"],
      defaultLabelPrefix: "Branch",
    });

    return normalized.length ? normalized : branchOptions;
  }, [vehicleCompanyBranchesDropdownData]);

  const vendorApiOptions = useMemo(() => {
    const source = Array.isArray(vehicleVendorsDropdownData)
      ? vehicleVendorsDropdownData
      : findCollectionByKeys(vehicleVendorsDropdownData, [
        "vehiclevendors",
        "vendors",
        "vehiclevendor",
        "vendor",
      ]);

    const normalized = normalizeOptions(source, {
      valueKeys: ["value", "id", "vendor_id"],
      labelKeys: ["title", "label", "vendor_name", "name"],
      defaultLabelPrefix: "Vendor",
    });

    return normalized.length ? normalized : vendorOptions;
  }, [vehicleVendorsDropdownData]);

  const makeApiOptions = useMemo(() => {
    const source = Array.isArray(vehicleMakesDropdownData)
      ? vehicleMakesDropdownData
      : findCollectionByKeys(vehicleMakesDropdownData, [
        "vehiclemakes",
        "makes",
        "vehiclemakesdropdown",
      ]);

    const normalized = normalizeOptions(source, {
      valueKeys: ["value", "id", "make_id"],
      labelKeys: ["label", "make_name", "name", "title"],
      defaultLabelPrefix: "Make",
    });

    return normalized.length ? normalized : vehicleMakeOptions;
  }, [vehicleMakesDropdownData]);

  const modelApiOptions = useMemo(() => {
    const source = Array.isArray(vehicleModelsDropdownData)
      ? vehicleModelsDropdownData
      : findCollectionByKeys(vehicleModelsDropdownData, [
        "vehiclemodels",
        "models",
        "vehiclemodelsdropdown",
      ]);

    const normalized = normalizeOptions(source, {
      valueKeys: ["value", "id", "model_id"],
      labelKeys: ["label", "model_name", "name", "title"],
      defaultLabelPrefix: "Model",
    });

    return normalized.length ? normalized : vehicleModelOptions;
  }, [vehicleModelsDropdownData]);

  const transmissionApiOptions = useMemo(() => {
    const source = Array.isArray(vehicleTransmissionTypesDropdownData)
      ? vehicleTransmissionTypesDropdownData
      : findCollectionByKeys(vehicleTransmissionTypesDropdownData, [
        "transmissiontypes",
        "vehicletransmissiontypes",
        "transmissiontypedropdown",
      ]);

    const normalized = normalizeOptions(source, {
      valueKeys: ["value", "id", "transmission_type_id"],
      labelKeys: ["label", "transmission_type_name", "name", "title"],
      defaultLabelPrefix: "Transmission Type",
    });

    return normalized.length ? normalized : transmissionTypeOptions;
  }, [vehicleTransmissionTypesDropdownData]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      if (!isEditing) {
        const payload = buildCreatePurchasedVehiclePayload({
          values,
          vehicleTypeApiOptions,
          branchApiOptions,
          vendorApiOptions,
          makeApiOptions,
          modelApiOptions,
          transmissionApiOptions,
        });
        await apiService.createPurchasedVehicle(payload);
      }

      successNotify(isEditing ? "Vehicle updated successfully" : "Vehicle created successfully");
      navigate(returnPath);
    } catch (error) {
      errorNotify(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={stepSchemasList[currentFormStep]}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        setFieldValue,
        validateForm,
        handleSubmit,
        setTouched,
        isSubmitting,
      }) => {
        const isLastStep = currentFormStep === formStepsList.length - 1;

        const updateAttachments = (updater) => {
          const nextFiles =
            typeof updater === "function"
              ? updater(values.attachments || [])
              : updater;
          setFieldValue("attachments", nextFiles);
        };

        const handleNext = async () => {
          const allErrors = await validateForm();
          const activeStepFields = stepFieldsList[currentFormStep] || [];
          const hasCurrentStepErrors = activeStepFields.some((fieldName) =>
            Object.prototype.hasOwnProperty.call(allErrors, fieldName)
          );

          if (hasCurrentStepErrors) {
            const touchedMap = {};
            activeStepFields.forEach((fieldName) => {
              touchedMap[fieldName] = true;
            });
            setTouched(touchedMap);
            return;
          }

          setCurrentFormStep((previous) => previous + 1);
        };

        const handlePrevious = () => {
          setCurrentFormStep((previous) => Math.max(0, previous - 1));
        };

        const handleTopSave = async () => {
          if (!isLastStep) {
            await handleNext();
            return;
          }
          handleSubmit();
        };

        return (
          <div className="create_vehicle_page">
            <Breadcrumbs values={["Assets", "Vehicles", "Create Vehicles"]} />

            <div className="page_header access_view">
              <h2>Create Vehicle</h2>

              <div className="d-flex align-items-center gap-2">
                <Button
                  cancelBtn={() => navigate(returnPath)}
                  submitBtn={handleTopSave}
                  submitText="Save"
                  type="button"
                  shouldRow={false}
                  loading={isSubmitting}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="page_container">
              <Row>
                <Col md={9}>
                  <div className="trip_information_main_div">
                    <h5>Vehicle Information</h5>

                    {resolvedDropdownError && (
                      <div
                        className="error-message"
                        style={{ color: "#ff0000de", marginBottom: "12px" }}
                      >
                        {resolvedDropdownError}
                      </div>
                    )}

                    <div>
                      <h6>
                        <span className="d-flex align-items-center gap-1">System ID: <span style={{ fontWeight: 500, marginLeft: "4px" }}>{values.systemId}</span> </span>
                      </h6>

                      <div className="d-flex trip_create_steps_layout">
                        <div className="trip_create_steps_sidebar">
                          <Steps
                            className="create_vehicle_steps form_steps_vertical"
                            current={currentFormStep}
                            direction="vertical"
                            items={formStepsList}
                            onChange={async (targetStep) => {
                              if (targetStep <= currentFormStep) {
                                setCurrentFormStep(targetStep);
                                return;
                              }

                              const allErrors = await validateForm();
                              const activeStepFields = stepFieldsList[currentFormStep] || [];
                              const hasCurrentStepErrors = activeStepFields.some((fieldName) =>
                                Object.prototype.hasOwnProperty.call(allErrors, fieldName)
                              );

                              if (hasCurrentStepErrors) {
                                const touchedMap = {};
                                activeStepFields.forEach((fieldName) => {
                                  touchedMap[fieldName] = true;
                                });
                                setTouched(touchedMap);
                                return;
                              }

                              setCurrentFormStep(targetStep);
                            }}
                          />
                        </div>

                        <div className="w-100 mb-3">
                          {currentFormStep === 0 && (
                            <Row>
                              <Col md={6}>
                                <Input
                                  label="Registration No."
                                  placeholder="Enter Registration No."
                                  name="registrationNumber"
                                  value={values.registrationNumber}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.registrationNumber}
                                  touched={touched.registrationNumber}
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="VIN / Chassis No."
                                  placeholder="Enter VIN / Chassis No."
                                  name="vinChassisNo"
                                  value={values.vinChassisNo}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.vinChassisNo}
                                  touched={touched.vinChassisNo}
                                />
                              </Col>
                              <Col md={6}>
                                <Select
                                  label="Make"
                                  placeholder="Select Make"
                                  options={makeApiOptions}
                                  value={
                                    findOptionByValueOrLabel(makeApiOptions, values.make)
                                  }
                                  name="make"
                                  onChange={(option) =>
                                    setFieldValue("make", getOptionFormValue(option, vehicleMakeOptions))
                                  }
                                  onBlur={handleBlur}
                                  error={errors.make}
                                  touched={touched.make}
                                  loading={dropdownLoading}
                                  isSearch={false}
                                />
                              </Col>
                              <Col md={6}>
                                <Select
                                  label="Model"
                                  placeholder="Select Model"
                                  options={modelApiOptions}
                                  value={
                                    findOptionByValueOrLabel(modelApiOptions, values.model)
                                  }
                                  name="model"
                                  onChange={(option) =>
                                    setFieldValue("model", getOptionFormValue(option, vehicleModelOptions))
                                  }
                                  onBlur={handleBlur}
                                  error={errors.model}
                                  touched={touched.model}
                                  loading={dropdownLoading}
                                  isSearch={false}
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="Year"
                                  type="number"
                                  placeholder="YYYY"
                                  name="year"
                                  value={values.year}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.year}
                                  touched={touched.year}
                                />
                              </Col>
                              <Col md={6}>
                                <Select
                                  label="Vehicle Type"
                                  placeholder="Select Type"
                                  options={vehicleTypeApiOptions}
                                  value={
                                    findOptionByValueOrLabel(
                                      vehicleTypeApiOptions,
                                      values.vehicleType
                                    )
                                  }
                                  name="vehicleType"
                                  onChange={(option) =>
                                    setFieldValue(
                                      "vehicleType",
                                      getOptionFormValue(option, vehicleTypeOptions)
                                    )
                                  }
                                  onBlur={handleBlur}
                                  error={errors.vehicleType}
                                  touched={touched.vehicleType}
                                  loading={dropdownLoading}
                                  isSearch={false}
                                />
                              </Col>
                              <Col md={6}>
                                <Select
                                  label="Fuel Type"
                                  placeholder="Select Fuel Type"
                                  options={fuelTypeOptions}
                                  value={
                                    fuelTypeOptions.find((option) => option.value === values.fuelType) ||
                                    null
                                  }
                                  name="fuelType"
                                  onChange={(option) =>
                                    setFieldValue("fuelType", option ? option.value : "")
                                  }
                                  onBlur={handleBlur}
                                  error={errors.fuelType}
                                  touched={touched.fuelType}
                                  isSearch={false}
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="Engine Capacity"
                                  placeholder="00"
                                  type="number"
                                  name="engineCapacity"
                                  value={values.engineCapacity}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.engineCapacity}
                                  touched={touched.engineCapacity}
                                  showExtraText
                                  extraText="CC"
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="Odometer Reading"
                                  placeholder="00"
                                  type="number"
                                  name="odometerReading"
                                  value={values.odometerReading}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.odometerReading}
                                  touched={touched.odometerReading}
                                  showExtraText
                                  extraText="km"
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="Tank Capacity"
                                  type="number"
                                  placeholder="00"
                                  name="tankCapacity"
                                  value={values.tankCapacity}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.tankCapacity}
                                  touched={touched.tankCapacity}
                                  showExtraText
                                  extraText="Litres"
                                />
                              </Col>
                              <Col md={6}>
                                <Select
                                  label="Transmission Type"
                                  placeholder="Select Transmission Type"
                                  options={transmissionApiOptions}
                                  value={
                                    findOptionByValueOrLabel(
                                      transmissionApiOptions,
                                      values.transmissionType
                                    )
                                  }
                                  name="transmissionType"
                                  onChange={(option) =>
                                    setFieldValue(
                                      "transmissionType",
                                      getOptionFormValue(option, transmissionTypeOptions)
                                    )
                                  }
                                  onBlur={handleBlur}
                                  error={errors.transmissionType}
                                  touched={touched.transmissionType}
                                  loading={dropdownLoading}
                                  isSearch={false}
                                />
                              </Col>
                              <Col md={6}>
                                <Select
                                  label="Branch"
                                  placeholder="Select Branch"
                                  options={branchApiOptions}
                                  value={
                                    findOptionByValueOrLabel(branchApiOptions, values.branch)
                                  }
                                  name="branch"
                                  onChange={(option) =>
                                    setFieldValue("branch", getOptionFormValue(option, branchOptions))
                                  }
                                  onBlur={handleBlur}
                                  error={errors.branch}
                                  touched={touched.branch}
                                  loading={dropdownLoading}
                                  isSearch={false}
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="Initial Fuel Efficiency"
                                  placeholder="00"
                                  type="number"
                                  name="initialFuelEfficiency"
                                  value={values.initialFuelEfficiency}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.initialFuelEfficiency}
                                  touched={touched.initialFuelEfficiency}
                                  showExtraText
                                  extraText="km/litre"
                                />
                              </Col>
                              <Col md={6}>
                                <Select
                                  label="Status"
                                  placeholder="Select Status"
                                  options={vehicleStatusOptions}
                                  value={
                                    vehicleStatusOptions.find(
                                      (option) => option.value === values.status
                                    ) || null
                                  }
                                  name="status"
                                  onChange={(option) =>
                                    setFieldValue("status", option ? option.value : "")
                                  }
                                  onBlur={handleBlur}
                                  error={errors.status}
                                  touched={touched.status}
                                  isSearch={false}
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="Number of Axles"
                                  placeholder="00"
                                  type="number"
                                  name="numberOfAxles"
                                  value={values.numberOfAxles}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.numberOfAxles}
                                  touched={touched.numberOfAxles}
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="IMEI Tracker Code"
                                  placeholder="00"
                                  type="number"
                                  name="imeino"
                                  value={values.imeino}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  // error={errors.imeino}
                                  touched={touched.imeino}
                                  required={false}
                                />
                              </Col>
                              <Col md={6}>
                                <Select
                                  label="Tracking Package"
                                  placeholder="Select Tracking Package"
                                  options={trackingPackageOptions}
                                  value={
                                    findOptionByValueOrLabel(
                                      trackingPackageOptions,
                                      values.trackingPackage
                                    ) || null
                                  }
                                  name="trackingPackage"
                                  onChange={(option) =>
                                    setFieldValue("trackingPackage", option ? option.value : "")
                                  }
                                  onBlur={handleBlur}
                                  error={errors.trackingPackage}
                                  touched={touched.trackingPackage}
                                  isSearch={false}
                                  isClearable
                                  required={hasImeiTrackerValue(values.imeino)}
                                />
                              </Col>
                              <Col md={12}>
                                <Input
                                  label="Description"
                                  placeholder="Write detail of part if any..."
                                  name="description"
                                  value={values.description}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.description}
                                  touched={touched.description}
                                  quill
                                  setFieldValue={setFieldValue}
                                  required={false}
                                  className="create_vehicle_description"
                                />
                              </Col>
                            </Row>
                          )}

                          {currentFormStep === 1 && !isHired && (
                            <Row>
                              <Col md={6}>
                                <Input
                                  label="Purchase Date"
                                  type="date"
                                  name="purchaseDate"
                                  value={values.purchaseDate}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.purchaseDate}
                                  touched={touched.purchaseDate}
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="Purchase Cost"
                                  type="number"
                                  placeholder="00"
                                  name="purchaseCost"
                                  value={values.purchaseCost}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.purchaseCost}
                                  touched={touched.purchaseCost}
                                  showExtraText
                                  extraText="PKR"
                                />
                              </Col>
                              <Col md={6}>
                                <Select
                                  label="Payment Type"
                                  placeholder="Select Payment Type"
                                  options={paymentTypeOptions}
                                  value={
                                    paymentTypeOptions.find(
                                      (option) => option.value === values.paymentType
                                    ) || null
                                  }
                                  name="paymentType"
                                  onChange={(option) =>
                                    setFieldValue("paymentType", option ? option.value : "")
                                  }
                                  onBlur={handleBlur}
                                  error={errors.paymentType}
                                  touched={touched.paymentType}
                                  isSearch={false}
                                />
                              </Col>
                              <Col md={6}>
                                <Select
                                  label="Payment Method"
                                  placeholder="Select Payment Method"
                                  options={paymentMethodOptions}
                                  value={
                                    paymentMethodOptions.find(
                                      (option) => option.value === values.paymentMethod
                                    ) || null
                                  }
                                  name="paymentMethod"
                                  onChange={(option) =>
                                    setFieldValue("paymentMethod", option ? option.value : "")
                                  }
                                  onBlur={handleBlur}
                                  error={errors.paymentMethod}
                                  touched={touched.paymentMethod}
                                  isSearch={false}
                                />
                              </Col>
                              <Col md={6}>
                                <Select
                                  label="Vendor"
                                  placeholder="Select Vendor"
                                  options={vendorApiOptions}
                                  value={
                                    findOptionByValueOrLabel(vendorApiOptions, values.vendor)
                                  }
                                  name="vendor"
                                  onChange={(option) =>
                                    setFieldValue("vendor", getOptionFormValue(option, vendorOptions))
                                  }
                                  onBlur={handleBlur}
                                  error={errors.vendor}
                                  touched={touched.vendor}
                                  loading={dropdownLoading}
                                  isSearch={false}
                                />
                              </Col>
                              <Col md={6}>
                                <Select
                                  label="Depreciation Method"
                                  placeholder="Select Depreciation Method"
                                  options={depreciationMethodOptions}
                                  value={
                                    depreciationMethodOptions.find(
                                      (option) => option.value === values.depreciationMethod
                                    ) || null
                                  }
                                  name="depreciationMethod"
                                  onChange={(option) =>
                                    setFieldValue("depreciationMethod", option ? option.value : "")
                                  }
                                  onBlur={handleBlur}
                                  error={errors.depreciationMethod}
                                  touched={touched.depreciationMethod}
                                  isSearch={false}
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="Useful Life"
                                  type="number"
                                  placeholder="00"
                                  name="usefulLife"
                                  value={values.usefulLife}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.usefulLife}
                                  touched={touched.usefulLife}
                                  showExtraText
                                  extraText="Years"
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="Residual Value"
                                  type="number"
                                  placeholder="00"
                                  name="residualValue"
                                  value={values.residualValue}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.residualValue}
                                  touched={touched.residualValue}
                                  showExtraText
                                  extraText="PKR"
                                />
                              </Col>

                              {isInstallmentPayment(values.paymentType) && (
                                <>
                                  <Col md={12}>
                                    <div className="create_vehicle_divider" />
                                    <h6 className="create_vehicle_subheading">
                                      Installments Details
                                    </h6>
                                  </Col>

                                  <Col md={6}>
                                    <Input
                                      label="Total Installments"
                                      type="number"
                                      placeholder="00"
                                      name="totalInstallments"
                                      value={values.totalInstallments}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      error={errors.totalInstallments}
                                      touched={touched.totalInstallments}
                                    />
                                  </Col>
                                  <Col md={6}>
                                    <Input
                                      label="Installment Amount"
                                      type="number"
                                      placeholder="00"
                                      name="installmentAmount"
                                      value={values.installmentAmount}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      error={errors.installmentAmount}
                                      touched={touched.installmentAmount}
                                      showExtraText
                                      extraText="PKR"
                                    />
                                  </Col>
                                  <Col md={6}>
                                    <Select
                                      label="Installment Frequency"
                                      placeholder="Select Installment Frequency"
                                      options={installmentFrequencyOptions}
                                      value={
                                        installmentFrequencyOptions.find(
                                          (option) =>
                                            option.value === values.installmentFrequency
                                        ) || null
                                      }
                                      name="installmentFrequency"
                                      onChange={(option) =>
                                        setFieldValue(
                                          "installmentFrequency",
                                          option ? option.value : ""
                                        )
                                      }
                                      onBlur={handleBlur}
                                      error={errors.installmentFrequency}
                                      touched={touched.installmentFrequency}
                                      isSearch={false}
                                    />
                                  </Col>
                                  <Col md={6}>
                                    <Input
                                      label="First Payment Date"
                                      type="date"
                                      name="firstPaymentDate"
                                      value={values.firstPaymentDate}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      error={errors.firstPaymentDate}
                                      touched={touched.firstPaymentDate}
                                    />
                                  </Col>
                                  <Col md={6}>
                                    <Input
                                      label="Interest Rate"
                                      type="number"
                                      placeholder="00"
                                      name="interestRate"
                                      value={values.interestRate}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      error={errors.interestRate}
                                      touched={touched.interestRate}
                                      showExtraText
                                      extraText="%"
                                    />
                                  </Col>
                                  <Col md={6}>
                                    <Input
                                      label="Financing Institution"
                                      placeholder="Enter Financing Institution"
                                      name="financingInstitution"
                                      value={values.financingInstitution}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      error={errors.financingInstitution}
                                      touched={touched.financingInstitution}
                                    />
                                  </Col>
                                </>
                              )}
                            </Row>
                          )}

                          {currentFormStep === 1 && isHired && (
                            <Row>
                              <Col md={6}>
                                <Select
                                  label="Vendor / Owner"
                                  placeholder="Select Vendor / Owner"
                                  options={vendorOptions}
                                  value={
                                    vendorOptions.find((option) => option.value === values.vendor) || null
                                  }
                                  name="vendor"
                                  onChange={(option) =>
                                    setFieldValue("vendor", option ? option.value : "")
                                  }
                                  onBlur={handleBlur}
                                  error={errors.vendor}
                                  touched={touched.vendor}
                                  isSearch={false}
                                  required
                                />
                              </Col>
                              <Col md={6}>
                                <Select
                                  label="Contract Type"
                                  placeholder="Select Type"
                                  options={contractTypeOptions}
                                  value={
                                    contractTypeOptions.find((option) => option.value === values.contractType) || null
                                  }
                                  name="contractType"
                                  onChange={(option) =>
                                    setFieldValue("contractType", option ? option.value : "")
                                  }
                                  onBlur={handleBlur}
                                  error={errors.contractType}
                                  touched={touched.contractType}
                                  isSearch={false}
                                  required
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="Contract Start Date"
                                  type="date"
                                  name="contractStartDate"
                                  value={values.contractStartDate}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.contractStartDate}
                                  touched={touched.contractStartDate}
                                  required
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="Contract End Date"
                                  type="date"
                                  name="contractEndDate"
                                  value={values.contractEndDate}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.contractEndDate}
                                  touched={touched.contractEndDate}
                                  required
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="Hire Rate"
                                  type="number"
                                  placeholder="00"
                                  name="hireRate"
                                  value={values.hireRate}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.hireRate}
                                  touched={touched.hireRate}
                                  showExtraText
                                  extraText="PKR"
                                  required
                                />
                              </Col>
                              <Col md={6}>
                                <Select
                                  label="Billing Frequency"
                                  placeholder="Select Frequency"
                                  options={billingFrequencyOptions}
                                  value={
                                    billingFrequencyOptions.find((option) => option.value === values.billingFrequency) || null
                                  }
                                  name="billingFrequency"
                                  onChange={(option) =>
                                    setFieldValue("billingFrequency", option ? option.value : "")
                                  }
                                  onBlur={handleBlur}
                                  error={errors.billingFrequency}
                                  touched={touched.billingFrequency}
                                  isSearch={false}
                                  required
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="Security Deposit"
                                  type="number"
                                  placeholder="00"
                                  name="securityDeposit"
                                  value={values.securityDeposit}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.securityDeposit}
                                  touched={touched.securityDeposit}
                                  showExtraText
                                  extraText="PKR"
                                  required
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="Max KM Allowance"
                                  type="number"
                                  placeholder="00"
                                  name="maxKmAllowance"
                                  value={values.maxKmAllowance}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.maxKmAllowance}
                                  touched={touched.maxKmAllowance}
                                  showExtraText
                                  extraText="km"
                                  required
                                />
                              </Col>
                            </Row>
                          )}

                          {currentFormStep === 2 && (
                            <Row>
                              <Col md={6}>
                                <Select
                                  label="Insurance Provider"
                                  placeholder="Select Insurance Provider"
                                  options={insuranceProviderOptions}
                                  value={
                                    insuranceProviderOptions.find(
                                      (option) => option.value === values.insuranceProvider
                                    ) || null
                                  }
                                  name="insuranceProvider"
                                  onChange={(option) =>
                                    setFieldValue(
                                      "insuranceProvider",
                                      option ? option.value : ""
                                    )
                                  }
                                  onBlur={handleBlur}
                                  error={errors.insuranceProvider}
                                  touched={touched.insuranceProvider}
                                  isSearch={false}
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="Policy Number"
                                  placeholder="Enter Policy Number"
                                  name="policyNumber"
                                  value={values.policyNumber}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.policyNumber}
                                  touched={touched.policyNumber}
                                />
                              </Col>
                              <Col md={6}>
                                <Select
                                  label="Coverage Type"
                                  placeholder="Select Coverage Type"
                                  options={coverageTypeOptions}
                                  value={
                                    coverageTypeOptions.find(
                                      (option) => option.value === values.coverageType
                                    ) || null
                                  }
                                  name="coverageType"
                                  onChange={(option) =>
                                    setFieldValue("coverageType", option ? option.value : "")
                                  }
                                  onBlur={handleBlur}
                                  error={errors.coverageType}
                                  touched={touched.coverageType}
                                  isSearch={false}
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="Coverage Amount"
                                  placeholder="00"
                                  type="number"
                                  name="coverageAmount"
                                  value={values.coverageAmount}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.coverageAmount}
                                  touched={touched.coverageAmount}
                                  showExtraText
                                  extraText="PKR"
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="Premium Amount"
                                  placeholder="00"
                                  type="number"
                                  name="premiumAmount"
                                  value={values.premiumAmount}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.premiumAmount}
                                  touched={touched.premiumAmount}
                                  showExtraText
                                  extraText="PKR"
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="Start Date"
                                  type="date"
                                  name="insuranceStartDate"
                                  value={values.insuranceStartDate}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.insuranceStartDate}
                                  touched={touched.insuranceStartDate}
                                />
                              </Col>
                              <Col md={6}>
                                <Input
                                  label="Expiry Date"
                                  type="date"
                                  name="insuranceExpiryDate"
                                  value={values.insuranceExpiryDate}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={errors.insuranceExpiryDate}
                                  touched={touched.insuranceExpiryDate}
                                />
                              </Col>
                            </Row>
                          )}

                          {currentFormStep === 3 && (
                            <Row>
                              <Col md={12}>
                                <div className="issue_file_upload_wrapper">
                                  <label>Attachment</label>
                                  <FileUpload
                                    destination="vehicle-registration"
                                    files={values.attachments}
                                    setFiles={updateAttachments}
                                    maxFileSize={10}
                                    maxFiles={5}
                                    className="issue_file_upload create_vehicle_upload"
                                  />
                                  {touched.attachments && errors.attachments && (
                                    <div
                                      className="error-message"
                                      style={{
                                        color: "#ff0000de",
                                        fontSize: "12px",
                                        marginTop: "3px",
                                        marginLeft: "5px",
                                      }}
                                    >
                                      {errors.attachments}
                                    </div>
                                  )}
                                </div>
                              </Col>
                            </Row>
                          )}

                          <Row>
                            <Col md={12}>
                              <div className="d-flex align-items-center gap-2">
                                <Button
                                  cancelBtn={handlePrevious}
                                  cancelBtnDisabled={currentFormStep === 0}
                                  submitText={isLastStep ? "Save" : "Next"}
                                  type="button"
                                  shouldRow={false}
                                  submitBtn={isLastStep ? handleSubmit : handleNext}
                                  cancelBtnText="Previous"
                                  loading={isSubmitting}
                                  disabled={isSubmitting}
                                />
                              </div>
                            </Col>
                          </Row>
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>

                <Col md={3}>
                  <div className="trip_notes_main_div create_vehicle_notes">
                    <h5>Notes</h5>
                    <ReactQuill
                      placeholder="Write notes here..."
                      modules={modules}
                      formats={formats}
                      value={values.notes || ""}
                      onChange={(content) => setFieldValue("notes", content)}
                      className="react_quill_input notes_quill_input quill-small-font trip_quill_input"
                    />
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        );
      }}
    </Formik>
  );
};

export default CreatePurchasedVehicle;
